import { Empty, Input } from "antd";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import { useAdvanceTable } from "hooks";
import usePrivileges from "hooks/access-control/usePrivileges";
import useRoles from "hooks/access-control/useRoles";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState, useMemo } from "react";
import { Col, Dropdown, Row, Spinner, Modal, Button, Form } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CreateOrUpdatePrivilegeModal from "./CreateOrUpdatePrivilegeModal";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import Guard from "../rbac/Guard";
import { PRIV } from "../rbac/privileges";

export default function PrivilegeManagement() {
  // 1. ALL HOOKS AT THE TOP
  const {
    privileges,
    addPrivilege,
    updatePrivilege,
    deletePrivilege,
    loading,
  } = usePrivileges();
  const { roles, assignPrivilegesToRole } = useRoles();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPriv, setSelectedPriv] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [privToDelete, setPrivToDelete] = useState(null);
  const [deletingPriv, setDeletingPriv] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const filteredPrivileges = useMemo(() => {
    return (privileges || []).filter(p => 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      p.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [privileges, searchTerm]);

const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { style: { width: '300px' }, className: "text-900 fw-bold" }, // Standard Bold Header
      },
      cell: ({ getValue }) => {
        const val = getValue() || "";
        return (
          <h6
            className="mb-0 text-900 fs-10 text-truncate d-inline-block fw-normal" 
            style={{ maxWidth: 200, cursor: 'help' }}
            title={val}
          >
            {val}
          </h6>
        );
      },
    },
    {
      accessorKey: "scope",
      header: "Scope",
      meta: {
        headerProps: { style: { width: '100px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center" },
      },
      cell: ({ getValue }) => {
        const value = getValue();
        const style = {
          backgroundColor: "#e8f7e3",
          color: "#2e7d32",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          lineHeight: 1.2,
          border: "1px solid #d5ebd0",
          cursor: "default",
          fontWeight: 600, // Pill remains semi-bold
          display: "inline-block",
          whiteSpace: "nowrap",
          verticalAlign: "middle"
        };
        return <span style={style}>{value}</span>;
      },
    },
    {
      accessorKey: "owner",
      header: "Owner",
      meta: {
        headerProps: { style: { width: '200px' }, className: "text-900 fw-bold" },
        cellProps: { className: "align-middle" },
      },
      cell: ({ getValue }) => {
        const text = getValue() || "-";
        return (
          <span
            className="text-500 d-inline-block fw-normal text-truncate"
            style={{ fontSize: 12, lineHeight: 1.4, maxWidth: 200 }}
            title={text}
          >
            {text}
          </span>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      meta: {
        headerProps: { style: { width: "110px" }, className: "text-900 text-center fw-bold" },
        cellProps: { className: "text-center align-middle" },
      },
      cell: ({ getValue }) => {
        const type = getValue() || "SYSTEM";
        const isProduct = type === "PRODUCT";
        return (
          <span
            className={`badge rounded-pill ${isProduct ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
          >
            {isProduct ? "Product" : "System"}
          </span>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      meta: {
        headerProps: { style: { width: '250px' }, className: "text-900 fw-bold" }, // Standard Bold Header
        cellProps: { className: "align-middle" },
      },
      cell: ({ getValue }) => {
        const text = getValue() || "No description provided";
        const displayText = text.length > 25 ? `${text.slice(0, 25)}...` : text;
        return (
          <span
            className="text-500 d-inline-block fw-normal" // Changed to normal weight
            style={{ fontSize: 12, lineHeight: 1.4, cursor: 'help' }} // Standardized size
            title={text}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: {
        headerProps: { style: { width: '50px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center" },
      },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2 fw-normal"> {/* Ensure dropdown text is normal weight */}
              <Dropdown.Item className="fs-10" onClick={() => { setSelectedPriv(item); setShowViewModal(true); }}>
                Assign to role
              </Dropdown.Item>
              <Guard privilege={PRIV.UPDATE_PRIVILEGES}>
                <Dropdown.Item className="fs-10" onClick={() => { setSelectedPriv(item); setShowUpdateModal(true); }}>
                  Edit
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_PRIVILEGES}>
                <Dropdown.Divider as="div" />
                <Dropdown.Item
                  className="fs-10 text-danger"
                  onClick={() => { setPrivToDelete(item.id); setShowDeleteModal(true); }}
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
    },
  ], [setSelectedPriv, setShowViewModal, setShowUpdateModal, setPrivToDelete, setShowDeleteModal]);

  // 3. INITIALIZE TABLE HOOK
  const table = useAdvanceTable({
    data: filteredPrivileges,
    columns,
    sortable: true,
    pagination: true,
    perPage: 50,
  });

  // 4. HANDLERS
  const handleAssignToRole = async () => {
    if (!selectedPriv || !selectedRoleId) return;
    try {
      setAssigning(true);
      await assignPrivilegesToRole(selectedRoleId, [String(selectedPriv.id)]);
      setShowViewModal(false);
    } finally {
      setAssigning(false);
      setSelectedRoleId("");
    }
  };

  const handleDelete = async () => {
    try {
      setDeletingPriv(privToDelete);
      await deletePrivilege(privToDelete);
    } finally {
      setDeletingPriv(null);
      setShowDeleteModal(false);
    }
  };

  // 5. RENDER
  return (
    <>
      <WidgetSectionTitle
        icon="key"
        title="Privileges"
        subtitle="Manage user privileges and control access across the platform."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="d-flex justify-content-end mb-3">
        <Guard privilege={PRIV.CREATE_PRIVILEGES}>
          <IconButton
            variant="success"
            size="sm"
            icon="plus"
            onClick={() => setShowAddModal(true)}
            className="text-xs fw-bold"
            style={{ color: "#fff" }}
          >
            <span className="ms-1">Add Privilege</span>
          </IconButton>
        </Guard>
      </div>

      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search privileges..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{
                borderRadius: 12,
                height: 30,
                flex: "1 1 300px",
                maxWidth: 300,
                fontSize: "13px",
                display: "flex",
                alignItems: "center"
              }}
            />
          </div>
        </Col>

        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{filteredPrivileges.length}</span> of <span className="fw-semibold text-dark">{privileges?.length || 0}</span>
          </div>
        </Col>
      </Row>

      <div className="position-relative">
        {loading && (
          <div 
            className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" 
            style={{ 
                zIndex: 2, 
                backgroundColor: privileges.length > 0 ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                minHeight: 400 
            }}
          >
            <div className="text-center">
                <Spinner animation="border" variant="success" size="sm" />
                <div className="mt-2 text-500 fs-10 fw-medium">Fetching privileges...</div>
            </div>
          </div>
        )}

        {filteredPrivileges.length > 0 ? (
          <AdvanceTableProvider {...table}>
            <div className="mx-n1 px-1">
              <AdvanceTable
                headerClassName="bg-light text-nowrap align-middle text-dark fs-10"
                rowClassName="align-middle white-space-nowrap fs-10"
                tableProps={{
                  bordered: true,
                  striped: true,
                  className: "mb-0 overflow-hidden",
                }}
              />
            </div>
            <div className="mt-3">
               <TablePaginationFooter
                  page={table.getState().pagination.pageIndex}
                  totalPages={table.getPageCount()}
                  totalElements={filteredPrivileges.length}
                  onPrev={table.getCanPreviousPage() ? () => table.previousPage() : undefined}
                  onNext={table.getCanNextPage() ? () => table.nextPage() : undefined}
                  pageSize={table.getState().pagination.pageSize}
                  onPageSizeChange={(size) => table.setPageSize(size)}
                  pageSizeOptions={[10, 20, 50, 100]}
                />
            </div>
          </AdvanceTableProvider>
        ) : !loading && (
          <div className="py-5 bg-white rounded-3 border border-200 text-center">
            <Empty description="No privileges found" />
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateOrUpdatePrivilegeModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSave={addPrivilege}
      />

      <CreateOrUpdatePrivilegeModal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        privilege={selectedPriv}
        onSave={updatePrivilege}
      />

      <Modal show={showViewModal} onHide={() => { setShowViewModal(false); setSelectedRoleId(""); }} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title style={{ fontSize: 15 }} className="ps-2 ms-1 fw-bold">
            Assign "{selectedPriv?.name}" to Role
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-0">
          <div className="mb-3 text-xs">
            <div><strong>Scope:</strong> <span>{selectedPriv?.scope}</span></div>
            {selectedPriv?.description && (
              <div className="mt-1"><strong>Description:</strong> <span>{selectedPriv.description}</span></div>
            )}
          </div>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="text-900 text-xs">Select Role</Form.Label>
              <Form.Select
                size="sm"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="text-xs"
              >
                <option value="">Choose a role...</option>
                {roles?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 justify-content-start">
          <Button variant="secondary" onClick={() => setShowViewModal(false)} size="sm" className="text-xs">
            Close
          </Button>
          <Guard privilege={PRIV.ASSIGN_PRIVILEGES}>
            <Button
              variant="success"
              size="sm"
              className="text-xs"
              onClick={handleAssignToRole}
              disabled={!selectedRoleId || assigning || !roles?.length}
            >
              {assigning ? "Assigning..." : "Assign"}
            </Button>
          </Guard>
        </Modal.Footer>
      </Modal>

      <ConfirmDeleteModal
        itemName="privilege"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        loading={deletingPriv === privToDelete}
      />
    </>
  );
}
